"use client"

import { useEffect, useRef, useState } from "react"
import { Html5Qrcode } from "html5-qrcode"
import { useTheme } from "@mui/material"

const QRScanner = ({ onScanSuccess, onScanFailure, onClose }) => {
  const html5QrCodeRef = useRef(null)
  const [isScanning, setIsScanning] = useState(false)
  const videoRef = useRef(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [cameraFacing, setCameraFacing] = useState("environment")
  const [flashOn, setFlashOn] = useState(false)
  const [error, setError] = useState(null)
  const theme = useTheme()
  const [scanDetected, setScanDetected] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  // Throttle logging scan failures
  let lastErrorTime = 0
  const ERROR_THROTTLE_INTERVAL = 2000

  const forceStopCamera = () => {
    const videoElement = document.querySelector("#reader video")
    if (videoElement && videoElement.srcObject) {
      const tracks = videoElement.srcObject.getTracks()
      tracks.forEach((track) => {
        track.stop()
        track.enabled = false
      })
      videoElement.srcObject = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }

  const toggleFlash = async () => {
    try {
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.applyVideoConstraints({
          advanced: [{ torch: !flashOn }],
        })
        setFlashOn(!flashOn)
      }
    } catch (error) {
      console.error("Flash toggle error:", error)
      // Flash might not be supported on all devices
    }
  }

  const switchCamera = async () => {
    const newFacing = cameraFacing === "environment" ? "user" : "environment"
    await stopScanner()
    setCameraFacing(newFacing)
    setTimeout(() => {
      startScanner(newFacing)
    }, 500)
  }

  const startScanner = async (facing = cameraFacing) => {
    setIsInitializing(true)
    setError(null)
    try {
      const html5QrCode = new Html5Qrcode("reader")
      html5QrCodeRef.current = html5QrCode

      const cameraConfig = { facingMode: "environment" }
      const scannerConfig = {
        fps: 15,
        qrbox: { width: 400, height: 400 },
      }

      await html5QrCode.start(
        cameraConfig,
        scannerConfig,
        (decodedText, decodedResult) => {
          onScanSuccess(decodedText, decodedResult)
          stopScanner() // Stop scanning after success
        },
        (errorMessage) => {
          const now = Date.now()
          if (now - lastErrorTime > ERROR_THROTTLE_INTERVAL) {
            //console.warn("Scan failure:", errorMessage);
            lastErrorTime = now
          }
          onScanFailure?.(errorMessage)
        },
      )

      setIsScanning(true)
      setIsInitializing(false)

      const videoElement = document.querySelector("#reader video")
      // display: none
      //videoElement.style.display = "none"
      if (videoElement) {
        videoRef.current = videoElement
      }
    } catch (error) {
      console.error("Scanner error:", error)
      setIsInitializing(false)
      if (error.name === "NotAllowedError") {
        alert("Camera access was denied. Please enable it in your browser settings.")
      }
      onScanFailure?.(error.message)
    }
  }

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current) {
        await html5QrCodeRef.current.stop()
        html5QrCodeRef.current = null
      }
      setIsScanning(false)
      if (onClose) {
        onClose()
      }
    } catch (error) {
      console.error("Stop error:", error)
    } finally {
      forceStopCamera()
    }
  }

  useEffect(() => {
    startScanner()
    return () => {
      stopScanner()
    }
  }, [])

  return (
    <div style={{ position: "relative" }}>
      {isScanning && (
        <div style={{ position: "absolute", top: "16px", right: "16px", zIndex: 1000 }}>
          <div
            style={{
              position: "relative",
              display: "inline-block",
            }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <button
              onClick={stopScanner}
              aria-label="Close Scanner"
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                backdropFilter: "blur(4px)",
                border: "2px solid rgba(255, 255, 255, 0.2)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)",
                transition: "all 0.2s ease",
                padding: 0,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "scale(1.1)"
                e.currentTarget.style.boxShadow = "0 6px 16px rgba(0, 0, 0, 0.3)"
                e.currentTarget.style.backgroundColor = "rgba(255, 59, 48, 0.8)"
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "scale(1)"
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.25)"
                e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.6)"
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Tooltip */}
            {showTooltip && (
              <div
                style={{
                  position: "absolute",
                  bottom: "-40px",
                  right: "0",
                  backgroundColor: "rgba(0, 0, 0, 0.8)",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                  backdropFilter: "blur(4px)",
                  animation: "fadeIn 0.2s ease-in-out",
                }}
              >
                Close Scanner
                <div
                  style={{
                    position: "absolute",
                    top: "-6px",
                    right: "18px",
                    width: "12px",
                    height: "12px",
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    transform: "rotate(45deg)",
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
      <div id="reader" style={{ width: "400px", height: "auto" }} />
    </div>
  )
}

export default QRScanner
